import type { PreviewAnnotationContext } from '@/lib/preview/annotation';
import type { ResearchReasoningEffort } from '@/lib/research-assistant/reasoning-effort';
import type {
  GitHubPreviewBrowserContextSnapshot,
  GitHubPreviewContextReference,
} from '@/lib/github/preview/browser-context-shared';
import type { CanvasContextReference, CanvasContextSnapshot } from '@/lib/canvas/shared';
import type { ProjectDesignSystemContextReference } from '@/lib/project-design-system/shared';
import type { DocumentAttachment } from '@/lib/research-assistant/document-attachments';

/**
 * Research Assistant - Type Definitions
 *
 * Central type definitions for the research assistant feature.
 */
import type { AgentSkillUsage, SelectedAgentSkill } from '@/lib/agent-skills/types';
import type {
  ResearchExecutionMode,
  ResearchRunAction,
  ResearchRunReceipt,
  ResearchRunStatus,
} from '@/lib/research-assistant/runtime/run-controller';

export type {
  GoalSkepticVerdict,
  GoalVerificationResult,
  ResearchExecutionMode,
  ResearchPlanQuestion,
  ResearchRunAction,
  ResearchRunPhase,
  ResearchRunReceipt,
  ResearchRunStatus,
  UserResearchPlan,
  UserResearchPlanRequirement,
} from '@/lib/research-assistant/runtime/run-controller';
export type { DocumentAttachment } from '@/lib/research-assistant/document-attachments';

// ============================================================================
// Constants
// ============================================================================

export const MODEL_CONFIG = {
  TOOL_RESULT_COMPRESSION_MAX_TOKENS: 8_000,
  TOOL_TASK_TIMEOUT_MS: 45_000,
  MAX_TOOL_CALLS: 512,
  MAX_CONCURRENT_TOOL_CALLS: 12,

  // Cross-provider context budgets; per-model limits are resolved at runtime.
  // Context tracking is managed by SESSION_CONFIG.MAX_CONTEXT_TOKENS
  MAX_SYSTEM_PROMPT_TOKENS: 600_000,
  MAX_OUTPUT_TOKENS: 32_000, // Generous output for detailed reports
  MAX_HISTORY_TOKENS: 50_000,

  // Per-source limits - allow full page content
  // Average page is 5-15k tokens, some can be 50k+
  // With 20 pages max, worst case ~1M tokens - still within budget
  MAX_TOKENS_PER_SOURCE: 100_000, // Allow full pages up to ~400k chars
  MIN_TOKENS_PER_SOURCE: 500,

  // Conservative cross-provider token estimate.
  CHARS_PER_TOKEN: 4,
} as const;

export type RuntimeLimitProfile = 'user_interactive' | 'audit_runner';

export interface RuntimeExecutionLimits {
  profile: RuntimeLimitProfile;
  maxToolCalls: number;
  maxAgentSteps: number;
  maxConcurrentToolCalls: number;
  toolTaskTimeoutMs: number;
  toolCompressionMaxTokens: number;
  answerCompressionMaxTokens: number;
  maxResultsPerPageTool: number;
  maxResultsPerCitationTool: number;
  maxCitationContentUrls: number;
  maxAnswersPerPlatform: number;
  maxTotalAnswers: number;
}

const WEBSITE_EDITING_RUNTIME_MINIMUMS = {
  maxToolCalls: 96,
} as const;

export function withWebsiteEditingRuntimeLimits(
  limits: RuntimeExecutionLimits,
  active: boolean
): RuntimeExecutionLimits {
  if (!active) return limits;
  return {
    ...limits,
    maxToolCalls: Math.max(limits.maxToolCalls, WEBSITE_EDITING_RUNTIME_MINIMUMS.maxToolCalls),
    maxAgentSteps: Math.max(limits.maxToolCalls, WEBSITE_EDITING_RUNTIME_MINIMUMS.maxToolCalls) + 1,
  };
}

const USER_INTERACTIVE_LIMIT_DEFAULTS = {
  maxToolCalls: MODEL_CONFIG.MAX_TOOL_CALLS,
  maxConcurrentToolCalls: MODEL_CONFIG.MAX_CONCURRENT_TOOL_CALLS,
  toolTaskTimeoutMs: 60_000,
  toolCompressionMaxTokens: 10_000,
  // Keep answer payloads mostly uncompressed in interactive chats.
  // Compression should be emergency-only near the model context ceiling.
  answerCompressionMaxTokens: 650_000,
  maxResultsPerPageTool: 30,
  maxResultsPerCitationTool: 30,
  maxCitationContentUrls: 15,
  maxAnswersPerPlatform: 50,
  maxTotalAnswers: 250,
} as const;

const AUDIT_RUNNER_LIMIT_DEFAULTS = {
  maxToolCalls: 160,
  maxConcurrentToolCalls: MODEL_CONFIG.MAX_CONCURRENT_TOOL_CALLS,
  toolTaskTimeoutMs: 45_000,
  toolCompressionMaxTokens: 8_000,
  answerCompressionMaxTokens: 120_000,
  maxResultsPerPageTool: 20,
  maxResultsPerCitationTool: 20,
  maxCitationContentUrls: 10,
  maxAnswersPerPlatform: 50,
  maxTotalAnswers: 250,
} as const;

function readLimit(
  profile: RuntimeLimitProfile,
  key: string,
  fallback: number,
  min: number,
  max: number,
  legacyKey?: string
): number {
  const profileKey = profile === 'audit_runner' ? 'AUDIT' : 'USER';
  const profileEnv = `RESEARCH_${profileKey}_${key}`;
  const globalEnv = `RESEARCH_${key}`;
  const env: Record<string, string | undefined> =
    typeof process !== 'undefined' ? (process.env as Record<string, string | undefined>) : {};
  const profileRaw =
    env[profileEnv] ?? (legacyKey ? env[`RESEARCH_${profileKey}_${legacyKey}`] : undefined);
  const globalRaw = env[globalEnv] ?? (legacyKey ? env[`RESEARCH_${legacyKey}`] : undefined);
  const chosenRaw = profileRaw ?? globalRaw;
  if (chosenRaw === undefined) return fallback;
  const parsed = Number(chosenRaw);
  if (!Number.isFinite(parsed)) return fallback;
  return Math.min(max, Math.max(min, Math.floor(parsed)));
}

/**
 * Runtime execution limits for orchestrator + tool execution.
 *
 * Env override format:
 * - Global: `RESEARCH_<KEY>` (e.g. RESEARCH_MAX_TOOL_CALLS)
 * - Profile-specific: `RESEARCH_USER_<KEY>` / `RESEARCH_AUDIT_<KEY>`
 *
 * Profile-specific env takes precedence over global env.
 */
export function resolveRuntimeExecutionLimits(
  profile: RuntimeLimitProfile = 'user_interactive'
): RuntimeExecutionLimits {
  const defaults =
    profile === 'audit_runner' ? AUDIT_RUNNER_LIMIT_DEFAULTS : USER_INTERACTIVE_LIMIT_DEFAULTS;

  const maxToolCalls = readLimit(profile, 'MAX_TOOL_CALLS', defaults.maxToolCalls, 1, 2_048);
  // One tool round per available call plus synthesis; no independent depth cutoff.
  const maxAgentSteps = maxToolCalls + 1;
  const maxConcurrentToolCalls = readLimit(
    profile,
    'MAX_CONCURRENT_TOOL_CALLS',
    defaults.maxConcurrentToolCalls,
    1,
    50
  );
  const toolTaskTimeoutMs = readLimit(
    profile,
    'TOOL_TASK_TIMEOUT_MS',
    defaults.toolTaskTimeoutMs,
    5_000,
    10 * 60_000,
    'SUB_AGENT_TASK_TIMEOUT_MS'
  );
  const toolCompressionMaxTokens = readLimit(
    profile,
    'TOOL_COMPRESSION_MAX_TOKENS',
    defaults.toolCompressionMaxTokens,
    1_000,
    64_000,
    'SUB_AGENT_COMPRESSION_MAX_TOKENS'
  );
  const minAnswerCompressionCap =
    profile === 'user_interactive'
      ? Math.max(toolCompressionMaxTokens, 120_000)
      : toolCompressionMaxTokens;
  const answerCompressionMaxTokens = readLimit(
    profile,
    'ANSWER_COMPRESSION_MAX_TOKENS',
    defaults.answerCompressionMaxTokens,
    minAnswerCompressionCap,
    650_000,
    'SUB_AGENT_ANSWER_COMPRESSION_MAX_TOKENS'
  );
  const maxResultsPerPageTool = readLimit(
    profile,
    'MAX_RESULTS_PER_PAGE_TOOL',
    defaults.maxResultsPerPageTool,
    1,
    100
  );
  const maxResultsPerCitationTool = readLimit(
    profile,
    'MAX_RESULTS_PER_CITATION_TOOL',
    defaults.maxResultsPerCitationTool,
    1,
    200
  );
  const maxCitationContentUrls = readLimit(
    profile,
    'MAX_CITATION_CONTENT_URLS',
    defaults.maxCitationContentUrls,
    1,
    200
  );
  const maxAnswersPerPlatform = readLimit(
    profile,
    'MAX_ANSWERS_PER_PLATFORM',
    defaults.maxAnswersPerPlatform,
    1,
    200
  );
  const maxTotalAnswers = readLimit(
    profile,
    'MAX_TOTAL_ANSWERS',
    defaults.maxTotalAnswers,
    maxAnswersPerPlatform,
    500
  );

  return {
    profile,
    maxToolCalls,
    maxAgentSteps,
    maxConcurrentToolCalls,
    toolTaskTimeoutMs,
    toolCompressionMaxTokens,
    answerCompressionMaxTokens,
    maxResultsPerPageTool,
    maxResultsPerCitationTool,
    maxCitationContentUrls,
    maxAnswersPerPlatform,
    maxTotalAnswers,
  };
}

/**
 * Source loading configuration.
 */
export const SOURCE_CONFIG = {
  MAX_ANSWERS: 50, // Allow broader answer sampling per engine
  MAX_CITATIONS: 15,
  MAX_PAGES: 20,
  MIN_CONTENT_LENGTH: 200,
} as const;

/**
 * Session configuration.
 * Context budget for compact assistant state stored on the parent session doc.
 *
 * Per-request research budget:
 * - System prompt: ~50k tokens
 * - Loaded content (pages/answers/citations): variable, tracked in loadedContext
 * - Conversation history: tracked in compact context messages
 * - Response generation: ~32k tokens
 *
 * MAX_CONTEXT_TOKENS is intentionally much smaller than the model's full context
 * window so the parent Firestore session doc stays compact. Full transcripts live
 * in a subcollection and are not part of this budget.
 */
export const SESSION_CONFIG = {
  // Token-based limits for compact assistant context persisted on the parent doc
  MAX_CONTEXT_TOKENS: 50_000,
  SUMMARIZE_THRESHOLD: 0.7, // Trigger summarization at 70% of max (35k tokens)
  SUMMARIZE_TARGET: 0.4, // Summarize down to 40% of max (20k tokens)

  // Session management
  SESSION_TTL_DAYS: 365,
  COLLECTION_NAME: 'research_sessions',
} as const;

// ============================================================================
// Source Types
// ============================================================================

export type SourceType = 'answer' | 'citation' | 'page';

export interface Source {
  id: string;
  type: SourceType;
  title: string;
  content: string;
  truncatedContent: string; // Token-budgeted version
  tokenCount: number;
  metadata: SourceMetadata;
}

export interface SourceMetadata {
  // Common
  url?: string;

  // Answer-specific
  engine?: string;
  promptText?: string;

  // Citation-specific
  citationCount?: number;
  pageTitle?: string;

  // Page-specific
  domain?: string;
}

export interface SourceCounts {
  answers: number;
  citations: number;
  pages: number;
  perceptionInsights?: number; // Brand perception insights count
  total: number;
  totalTokens: number;
}

// ============================================================================
// Message Types
// ============================================================================

export type ToolUsageStatus = 'preparing' | 'calling' | 'done' | 'error';
export type ToolExecutionMode = 'direct' | 'sub_agent';
export type ResearchToolFailureKind =
  | 'timeout_or_cancelled'
  | 'query_error'
  | 'validation_error'
  | 'rate_limit'
  | 'auth_error'
  | 'not_found'
  | 'conflict'
  | 'transient_error'
  | 'permanent_error';

export interface SubAgentMetadata {
  id: string;
  label: string;
  model?: string;
}

/**
 * Image attachment for chat messages.
 * Supports screenshots, uploaded images, and pasted images.
 */
export interface ImageAttachment {
  slackImageStagingId?: string;
  /** Browser draft cleanup scope; never a server authorization grant. */
  workspaceImageSource?: { workspaceId: string; sessionId: string };
  id: string;
  mimeType: 'image/png' | 'image/jpeg' | 'image/webp' | 'image/gif';
  base64Data: string; // Base64-encoded image data (without data: prefix)
  name?: string; // Original filename if available
  width?: number;
  height?: number;
  websiteMediaAssetId?: string;
  previewAnnotation?: PreviewAnnotationContext;
}

export interface PersistedImageAttachment {
  slackImageStagingId?: string;
  id: string;
  mimeType: ImageAttachment['mimeType'];
  name?: string;
  width?: number;
  height?: number;
  sizeBytes: number;
  sha256: string;
  websiteMediaAssetId?: string;
  previewAnnotation?: PreviewAnnotationContext;
}

/**
 * Source reference from tool execution.
 * Used to display clickable links to citations/answers/pages.
 * Persisted with messages to enable trace viewing on session reload.
 */
export interface SourceReference {
  type: 'citation' | 'answer' | 'page' | 'brand_perception';
  url?: string; // For citations and pages
  title?: string; // For citations and pages
  engine?: string; // For AI answers and brand perception
  citationCount?: number; // How many times cited
  answerId?: string; // For AI answers - enables deep linking to specific answer
  evidenceItemId?: string; // Exact captured result card
  projectId?: string;
  runIndex?: number; // For AI answers - run index within the engine
  wordCount?: number; // For AI answers and pages - content length
  contentPreview?: string; // For AI answers - first ~100 chars of content
  perceptionType?: string; // For brand perception (bp_fit, bp_ownership, etc.)
  label?: string; // Human-readable label for brand perception
}

/**
 * Generate a unique deduplication key for a source reference.
 * Uses multiple fallback strategies to ensure consistent deduplication across server and client.
 *
 * @param src - The source reference to generate a key for
 * @param fallbackCounter - Optional counter for sources without unique identifiers (defaults to 0)
 * @returns A unique string key for deduplication
 */
export function getSourceDeduplicationKey(
  src: SourceReference,
  fallbackCounter: number = 0
): string {
  // Answers: answerId → engine+runIndex → unique counter
  if (src.type === 'answer') {
    if (src.answerId) {
      return src.evidenceItemId
        ? `answer:${src.answerId}:evidence:${src.evidenceItemId}`
        : `answer:${src.answerId}`;
    }
    if (src.engine && src.runIndex !== undefined) {
      return `answer:${src.engine}:run${src.runIndex}`;
    }
    // Fallback: generate unique key to prevent data loss
    return `answer:uniq:${fallbackCounter}`;
  }

  // Others: url → type+title → unique counter
  if (src.url) {
    return `${src.type}:${src.url}`;
  }
  if (src.title) {
    return `${src.type}:title:${src.title}`;
  }
  // Fallback: generate unique key to prevent data loss
  return `${src.type}:uniq:${fallbackCounter}`;
}

export interface ToolUsageItem {
  callId?: string;
  tool: string;
  status: ToolUsageStatus;
  count?: number;
  error?: string;
  resultState?:
    | 'ok'
    | 'no_results'
    | 'coverage_gap'
    | 'fallback_used'
    | 'timeout_or_cancelled'
    | 'error';
  failureKind?: ResearchToolFailureKind;
  fallbackUsed?: {
    fromTool: string;
    toTool: string;
    reason: string;
  };
  coverageGap?: {
    citationScopeCount?: number;
    citationPageCount?: number;
    reason?: string;
  };
  metadata?: Record<string, unknown>;
  args?: Record<string, unknown>; // Tool arguments (for displaying search params in UI)
  sources?: SourceReference[]; // Sources fetched by this tool
  executionMode?: ToolExecutionMode;
  subAgent?: SubAgentMetadata;
}

export interface ReasoningTraceTimelineItem {
  type: 'reasoning';
  id: string;
  content: string;
}

export interface ToolTraceTimelineItem extends ToolUsageItem {
  type: 'tool';
  id: string;
}

export interface ProgressTraceTimelineItem {
  type: 'progress';
  id: string;
  content: string;
}

export type TraceTimelineItem =
  | ReasoningTraceTimelineItem
  | ToolTraceTimelineItem
  | ProgressTraceTimelineItem;

export interface ChatMessage {
  actorUserId?: string;
  actorDisplayName?: string;
  /** Server-only reference to the immutable outcome that resumed this turn. */
  slackContinuation?: {
    id: string;
    status: string;
    action?: string;
    turnId?: string;
    toolCallId?: string;
  };
  id: string;
  role: 'user' | 'model';
  content: string;
  timestamp: string;
  tokenCount?: number;
  modelId?: string;
  targetModelId?: string;
  reasoningEffort?: ResearchReasoningEffort;
  selectedSkills?: SelectedAgentSkill[];
  provider?: string;
  costUsd?: number;
  usage?: import('@/lib/research-assistant/models').ResearchUsageBreakdown[];
  commandId?: string; // If message was from a command
  activeReportId?: string | null; // Immutable Doc target used by this turn and its retries
  websitePreviewContext?: GitHubPreviewBrowserContextSnapshot;
  websitePreviewContextRef?: GitHubPreviewContextReference;
  canvasContext?: CanvasContextSnapshot;
  canvasContextRef?: CanvasContextReference;
  designSystemContextRef?: ProjectDesignSystemContextReference;
  skillUsage?: AgentSkillUsage[]; // Immutable, bounded Skill version snapshots for this turn
  toolUsage?: ToolUsageItem[]; // Proof-of-work: tools used to generate this message
  sources?: SourceReference[]; // Aggregated sources from all tools (persisted for trace viewing)
  images?: ImageAttachment[]; // Attached images (screenshots, uploads)
  imageAttachments?: PersistedImageAttachment[]; // Durable metadata for images staged outside Firestore
  documents?: DocumentAttachment[]; // Metadata-only references to uploaded documents
  thinkingContent?: string; // Provider reasoning summary displayed to the user.
  traceTimeline?: TraceTimelineItem[]; // Ordered reasoning/tool trace for interleaved UX rendering
  subagents?: import('./subagent-status').SubagentStatusRow[];
  executionMode?: ResearchExecutionMode;
  turnId?: string;
  taskRunId?: string;
  runId?: string;
  runAction?: Exclude<ResearchRunAction, 'cancel'>;
  expectedSequence?: number;
  runStatus?: ResearchRunStatus;
  runReceipt?: ResearchRunReceipt;
  inputKind?: 'message' | 'steer';
  steerId?: string;
  steerSequence?: number;
  steeringEpoch?: number;
}

// ============================================================================
// Session Types (defined in session-manager.ts)
// ============================================================================

// Session and SessionSummary are exported from session-manager.ts.

// ============================================================================
// API Types
// ============================================================================

export type ResearchSessionMode = 'brand_perception' | 'onboarding';

export type ApiAction =
  | 'create_session'
  | 'send_message'
  | 'preview_capture_result'
  | 'steer_turn'
  | 'cancel_turn'
  | 'cancel_run'
  | 'run_system_command'
  | 'record_system_command_outcome'
  | 'list_models'
  | 'list_system_audit_commands'
  | 'get_session'
  | 'list_sessions'
  | 'list_workspace_slack_sessions'
  | 'list_sessions_by_ids'
  | 'list_shared_sessions'
  | 'get_workspace_rail_bootstrap'
  | 'update_session_sharing'
  | 'fork_session'
  | 'get_thread_organization'
  | 'update_thread_organization'
  | 'list_archived_sessions'
  | 'archive_session'
  | 'archive_all_sessions'
  | 'restore_session'
  | 'delete_session'
  | 'rename_session'
  | 'suggest_session_name'
  | 'auto_name_session'
  | 'create_export'
  | 'get_export'
  | 'save_export_artifact'
  | 'list_exports'
  | 'retry_export'
  | 'delete_export';

/**
 * Visibility context passed from sidebars to provide AI with dashboard metrics.
 * Supports both prompt visibility metrics and publisher citation metrics.
 */
export interface VisibilityContext {
  // Prompt visibility metrics
  visibility_pct?: number;
  mentions?: number;
  total_runs?: number;
  rank?: number | null;
  // Publisher citation metrics (from CitationContext)
  domain?: string;
  total_citations?: number;
  unique_pages?: number;
  share_pct?: number;
  mentions_brand_count?: number;
  onboarding_workspace_id?: string | null;
}

export interface ApiRequest {
  scope?: import('./session-scope').ResearchSessionScope;
  action: ApiAction;
  previewCaptureClient?: boolean;
  titleGenerationStarted?: boolean;
  captureRequestId?: string;
  captureResult?: unknown;
  sessionId?: string;
  promptId?: string;
  promptText?: string; // Optional prompt text override (used by Slack integration)
  jobId?: string; // Optional sampler job scope (used by internal automation)
  jobIds?: string[]; // Optional multi-job scope (selected week/month report scope)
  projectId?: string;
  workspaceId?: string;
  migrationTargetScope?: 'workspace' | 'project';
  userConfirmed?: boolean;
  mode?: ResearchSessionMode; // Special session modes.
  visibilityContext?: VisibilityContext; // Dashboard metrics to pass to AI
  deferHydration?: boolean; // Skip expensive session metadata pre-computation during bootstrap
  includeBootstrapSession?: boolean; // Include a session payload suitable for first-render hydration
  reuseEmptySession?: boolean; // Opt-in only: reuse the newest empty default session instead of creating a new one
  externalSessionId?: string; // Trusted internal callers may supply a deterministic idempotency key
  // Pagination (used by list_sessions)
  limit?: number;
  startAfter?: string; // ISO timestamp cursor (typically SessionSummary.updatedAt)
  slackThreadMode?: 'stored' | 'discover';
  cursor?: string; // Opaque cursor used by shared-session pagination
  includeThreadOrganization?: boolean;
  visibility?: 'private' | 'workspace';
  sessionIds?: string[];
  threadOrganization?: unknown;
  engine?: string;
  message?: string;
  executionMode?: ResearchExecutionMode;
  turnId?: string;
  expectedTurnId?: string;
  steerId?: string;
  runId?: string;
  runAction?: ResearchRunAction;
  expectedSequence?: number;
  modelId?: string;
  targetModelId?: string;
  reasoningEffort?: ResearchReasoningEffort;
  images?: ImageAttachment[]; // Image attachments for send_message
  documents?: DocumentAttachment[]; // Uploaded document references for send_message
  commandId?: string; // CommandId is validated at runtime
  selectedSkills?: SelectedAgentSkill[]; // IDs only; scope/content/version are resolved server-side
  variables?: Record<string, unknown>; // Variable interpolation for system command templates
  autoTrack?: boolean; // Automation mode: create approved Growth Inbox issues
  auditRunId?: string; // Optional project_audit_runs linkage
  attempt?: number; // Optional background command attempt number
  completed?: boolean; // Internal background task outcome
  recovered?: boolean; // Internal outcome recovered after transport failure
  error?: string; // Internal background task error summary
  failureCode?: string; // Internal bounded non-sensitive outcome reason
  label?: string;
  description?: string;
  icon?: string;
  promptTemplate?: string;
  // Export-related
  exportId?: string;
  title?: string;
  format?: 'markdown' | 'infographic_series' | 'workspace_delivery' | 'html_slides' | 'html_report';
  status?: 'pending' | 'processing' | 'ready' | 'failed';
  template?: string;
  renderConfig?: {
    panelCount?: number;
    candidatesPerPanel?: number;
    refinement?: boolean;
    workerConcurrency?: number;
    width?: number;
    height?: number;
  };
  externalTargets?: {
    googleDoc?: {
      enabled: boolean;
    };
    googleSheet?: {
      enabled: boolean;
    };
    googleSlides?: {
      enabled: boolean;
    };
  };
  sourceMessageId?: string;
  activeReportId?: string;
  websitePreviewContext?: GitHubPreviewBrowserContextSnapshot;
  websitePreviewContextRef?: GitHubPreviewContextReference;
  canvasContext?: CanvasContextSnapshot;
  canvasContextRef?: CanvasContextReference;
  canvasEditingActive?: boolean;
  designSystemEditingActive?: boolean;
  artifactId?: string;
  // Rename-related
  name?: string; // User-defined session name (for rename_session)
}

export interface ApiError {
  code: string;
  message: string;
  details?: Record<string, unknown>;
}

// ============================================================================
// Utility Types
// ============================================================================

export interface TokenBudget {
  total: number;
  used: number;
  remaining: number;
  perSource: number;
}
